import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Reusable in-app notification bell.
 *
 * Works for both admin & employer sessions — the /api/notifications endpoint
 * infers the recipient from the JWT (admin OR employer) and returns the
 * correct inbox. Polls every 30 seconds while the tab is open.
 *
 * Props:
 *   variant: "light" | "dark"  → controls text/border colour on the icon
 *   pollMs:  number             → poll frequency (default 30 000)
 */
export default function NotificationBell({ variant = "light", pollMs = 30000 }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const prevUnread = useRef(0);
  const wrapRef = useRef(null);

  const hasSession = useMemo(() => {
    return !!(localStorage.getItem("adelina_token") || localStorage.getItem("adelina_emp_token"));
  }, []);

  const fetchInbox = useCallback(async () => {
    if (!hasSession) return;
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 40 } });
      setItems(data.items || []);
      const nextUnread = data.unread ?? 0;
      // Play a soft toast on new arrivals so the user sees them without opening the bell
      if (nextUnread > prevUnread.current && prevUnread.current !== 0) {
        const latest = (data.items || []).find((n) => !n.read);
        if (latest) toast.info(`${latest.title}`, { description: latest.message });
      }
      prevUnread.current = nextUnread;
      setUnread(nextUnread);
    } catch (_e) {
      // Silent — bell is auxiliary UI, not critical
    } finally { setLoading(false); }
  }, [hasSession]);

  useEffect(() => {
    if (!hasSession) return;
    fetchInbox();
    const t = setInterval(fetchInbox, pollMs);
    return () => clearInterval(t);
  }, [hasSession, pollMs, fetchInbox]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const markRead = async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
    try { await api.post(`/notifications/${id}/read`); } catch (_e) { /* ignore */ }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch (_e) { /* ignore */ }
  };

  if (!hasSession) return null;

  const kindColor = (k) => ({
    success: "text-emerald-700 bg-emerald-50 border-emerald-200",
    warning: "text-amber-800 bg-amber-50 border-amber-200",
    action:  "text-[#8b6508] bg-[#b8860b]/10 border-[#b8860b]/30",
    info:    "text-sky-800 bg-sky-50 border-sky-200",
  }[k] || "text-zinc-700 bg-zinc-50 border-zinc-200");

  const btnClass = variant === "dark"
    ? "text-white hover:bg-white/10"
    : "text-zinc-800 hover:bg-zinc-100";

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`relative inline-flex items-center justify-center w-9 h-9 rounded-full border border-zinc-300 transition-colors ${btnClass}`}
        data-testid="notif-bell-btn"
        aria-label="Notifications"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center border border-white"
            data-testid="notif-unread-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] max-w-[92vw] bg-white border border-zinc-200 rounded-2xl shadow-xl z-50 overflow-hidden"
          data-testid="notif-dropdown"
        >
          <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
            <div>
              <div className="font-serif text-lg text-zinc-950">Notifications</div>
              <div className="text-[11px] text-zinc-500">{unread} unread</div>
            </div>
            <button
              type="button"
              onClick={markAll}
              disabled={unread === 0}
              className="inline-flex items-center gap-1 text-[11px] text-zinc-700 hover:text-[#8b6508] disabled:opacity-40"
              data-testid="notif-read-all"
            >
              <CheckCheck size={12} /> Mark all read
            </button>
          </div>

          <div className="max-h-[420px] overflow-auto">
            {loading && items.length === 0 && (
              <div className="p-6 text-center text-zinc-500 text-sm inline-flex items-center gap-2 w-full justify-center">
                <Loader2 className="animate-spin" size={14} /> Loading…
              </div>
            )}
            {!loading && items.length === 0 && (
              <div className="p-8 text-center text-zinc-500 text-sm">No notifications yet.</div>
            )}
            {items.map((n) => {
              const clickable = !!n.link;
              const activate = async () => {
                // Mark as read FIRST (before closing / navigating) so the API
                // call actually fires even when the click also unmounts the
                // dropdown. Optimistic update is applied inside markRead.
                if (!n.read) {
                  try { await markRead(n.id); } catch (_e) { /* best-effort */ }
                }
                setOpen(false);
                if (!n.link) return;
                // Absolute URLs → new tab. Internal paths → SPA navigate so
                // the user gets an instant transition (no full page reload)
                // and any query-string params (e.g. ?booking=…) reach the
                // target tab correctly.
                if (/^https?:\/\//i.test(n.link)) {
                  window.open(n.link, "_blank", "noopener");
                } else {
                  navigate(n.link);
                }
              };
              return (
                <div
                  key={n.id}
                  onClick={clickable ? activate : undefined}
                  role={clickable ? "button" : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); activate(); } } : undefined}
                  className={
                    "px-4 py-3 border-b border-zinc-50 flex items-start gap-3 " +
                    (n.read ? "opacity-60 " : "") +
                    (clickable ? "cursor-pointer hover:bg-[#b8860b]/5 focus:bg-[#b8860b]/10 outline-none" : "")
                  }
                  data-testid={`notif-item-${n.id}`}
                >
                  <span className={`shrink-0 mt-0.5 w-2 h-2 rounded-full ${n.read ? "bg-zinc-300" : "bg-[#b8860b]"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${kindColor(n.kind)}`}>
                        {n.kind || "info"}
                      </span>
                      <span className="text-[10.5px] text-zinc-400">
                        {new Date(n.created_at).toLocaleString()}
                      </span>
                      {clickable && (
                        <span className="text-[9.5px] text-[#8b6508] font-semibold uppercase tracking-wider ml-auto">
                          Open →
                        </span>
                      )}
                    </div>
                    <div className="font-semibold text-zinc-950 text-[13.5px] mt-1">{n.title}</div>
                    <div className="text-[12.5px] text-zinc-700 mt-0.5 break-words">{n.message}</div>
                  </div>
                  {!n.read && (
                    <button
                      onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                      className="text-zinc-400 hover:text-[#8b6508]"
                      title="Mark as read"
                      data-testid={`notif-mark-${n.id}`}
                    >
                      <Check size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
